import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import ReportIcon from '@mui/icons-material/Report';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { endOfDay, startOfDay } from 'date-fns';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	ConditionalWrapper,
	DataGrid,
	DataGridSortModel,
	FilterAutoCompleteById,
	FilterSearch,
	FilterSelect,
	RequirementChip,
	GeneralTooltip as Tooltip,
	Spinner,
} from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { GroupForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroups, createGroup } from 'src/api/personal/groups';
import { getGroupTypes } from 'src/api/admin/types';
import { getRolesForAutocomplete } from 'src/api/admin/roles';

interface SearchParams {
	sort?: string;
	page?: number;
	search?: string;
	typeIds?: string;
	groupStatus?: string;
}

export const Route = createFileRoute('/personal_/groups/')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		sort: search.sort as string,
		page: (search.page as number) || 0,
		search: search.search as string,
		typeIds: search.typeIds as string,
		groupStatus: search.groupStatus as string,
	}),
	component: GroupsPage,
});

function GroupsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const search = useSearch({ from: '/personal_/groups/' });
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { updateValue } = useLocalStorage<string>('personalDashboardGroupsBackUrl');
	const [isFormOpened, setIsFormOpened] = useState(false);

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Companies');

	const hasGroupsPermission = verifyPermission([PERMISSIONS.personalGroupsView]);

	useEffect(() => {
		if (!authLoading && !hasGroupsPermission) {
			navigate({ to: '/personal/overview' });
		}
	}, [authLoading, hasGroupsPermission, navigate]);

	const queryParams = useMemo(() => {
		const params: Record<string, string> = {};
		if (search.sort) params.sort = search.sort;
		if (search.page) params.page = search.page;
		if (search.search) params.search = search.search;
		if (search.typeIds) params.typeIds = search.typeIds;
		if (search.groupStatus) params.groupStatus = search.groupStatus;
		return params;
	}, [search]);

	const {
		data: groupsData,
		isLoading: groupsLoading,
		refetch: refetchGroups,
	} = useQuery({
		queryKey: ['personal-groups', queryParams],
		queryFn: () => getGroups(queryParams),
		enabled: !authLoading && hasGroupsPermission,
	});

	const { data: typesData } = useQuery({
		queryKey: ['group-types'],
		queryFn: () => getGroupTypes(),
	});

	const { data: rolesData } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: () => getRolesForAutocomplete(),
	});

	const types = typesData?.data || [];
	const roles = rolesData || [];

	const updateQueryParams = useCallback(
		(params: Partial<SearchParams>) => {
			navigate({
				to: '/personal/groups',
				search: (prev) => ({ ...prev, ...params }),
			});
		},
		[navigate],
	);

	const handleSort = ([sort]: DataGridSortModel) => {
		updateQueryParams({ sort: sort ? sortToString(sort) : undefined });
	};

	const handleRowClick = ({ row }: { row: { id: string } }) => {
		updateValue(typeof window !== 'undefined' ? window.location.href : '');
		navigate({ to: `/personal/groups/${row.id}` });
	};

	const handleSearchChange = debounce((searchValue: string) => {
		updateQueryParams({ search: searchValue || undefined });
	}, 350);

	const handleFilterChange = (filterData: { typeIds?: string[] | string; groupStatus?: string[] | string }) => {
		updateQueryParams({
			typeIds: Array.isArray(filterData.typeIds) ? filterData.typeIds.join(',') : filterData.typeIds,
			groupStatus: Array.isArray(filterData.groupStatus) ? filterData.groupStatus.join(',') : filterData.groupStatus,
		});
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleAddGroup = async (formData: Record<string, unknown> | undefined) => {
		let isError = false;
		if (formData) {
			try {
				const body = await createGroup(formData);
				if (body.userCreationError) {
					const failedEmails = body.userCreationError.emails?.join(', ');
					const message = failedEmails
						? `Company created successfully, but failed to create owner accounts for: ${failedEmails}. You can add owners manually later.`
						: 'Company created successfully, but some owner accounts could not be created. You can add owners manually later.';
					EventEmitter.emit('toast', { type: 'warning', message });
				} else {
					EventEmitter.emit('toast', { type: 'success', message: 'Company saved' });
				}
				queryClient.invalidateQueries({ queryKey: ['personal-groups'] });
				refetchGroups();
				setIsFormOpened(false);
				navigate({ to: `/personal/groups/${body.id}` });
			} catch {
				isError = true;
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Failed to save Company. Did you enter all data correctly?',
				});
			}
		}
		if (!isError) {
			setIsFormOpened(false);
		}
	};

	const logTypes = [
		{ value: 'all', label: 'All' },
		{ value: 'pending', label: 'Pending' },
		{ value: 'inReview', label: 'In Review' },
		{ value: 'accepted', label: 'Accepted' },
		{ value: 'expired', label: 'Expired' },
		{ value: 'expiryWarning', label: 'Expiry Warning' },
	];

	const columns = [
		{
			field: 'markedForReview',
			headerName: '',
			renderCell: (c: { row: { markedForReview?: boolean } }) =>
				c.row.markedForReview && <ReportIcon fontSize="small" color="error" />,
			width: 5,
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			renderCell: (c) => <RequirementChip object={c.row} />,
			sortable: false,
		},
		{
			field: 'uniqueId',
			headerName: 'VAT',
			flex: 1,
		},
		{
			field: 'description',
			headerName: 'Description',
			flex: 2,
		},
	];

	const defaultGroupData = {
		startTime: startOfDay(new Date()),
		finishTime: endOfDay(new Date()),
		adminIds: [],
	};

	if (authLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<div className="px-4 flex justify-center">
				<div className="max-w-[1280px] w-full">
					<div className="flex flex-col gap-2 mt-2">
						<div className="flex flex-row gap-2">
							<FilterSearch
								label="Search Company"
								defaultValue={search.search || ''}
								onChange={(searchInput: string) => handleSearchChange(searchInput)}
								sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
							/>
							<ConditionalWrapper
								Wrapper={({ children }) => (
									<Tooltip text="This action is not available for your role">{children}</Tooltip>
								)}
								condition={!verifyPermission([PERMISSIONS.personalGroupsCreate])}
							>
								<Button
									disabled={!verifyPermission([PERMISSIONS.personalGroupsCreate])}
									startIcon={<AddIcon />}
									variant="outlined"
									onClick={() => setIsFormOpened(true)}
									sx={{ pointerEvents: 'auto !important', flexShrink: 0 }}
								>
									Add company
								</Button>
							</ConditionalWrapper>
						</div>
						<div className="flex flex-row gap-2">
							<FilterAutoCompleteById
								key="typeFilter"
								label="Filter Types"
								options={types}
								value={search.typeIds || []}
								onChange={(typeIds) => handleFilterChange({ typeIds })}
								multiple
							/>
							<FilterSelect
								key="groupStatus"
								listSubHeader="Filter Company Statuses"
								options={logTypes}
								value={search.groupStatus || ['all']}
								onChange={(groupStatus) => handleFilterChange({ groupStatus })}
								sx={{ width: '100%', backgroundColor: 'white' }}
							/>
						</div>
					</div>

					<Paper variant="outlined" sx={{ marginTop: 2 }}>
						<DataGrid
							data={groupsData?.data || []}
							columns={columns}
							rowCount={groupsData?.count || 0}
							paginationMode="server"
							sortingMode="server"
							isLoading={groupsLoading}
							onPaginationModelChange={handlePaginationModelChange}
							onSortModelChange={handleSort}
							sortingOrder={['asc', 'desc']}
							sortModel={[stringToSort((search.sort || 'startTime:desc') as `${string}:${string}`)]}
							pageSize={groupsData?.pageSize || 25}
							pageSizeOptions={[groupsData?.pageSize || 25]}
							page={groupsData?.page ?? 0}
							onRowClick={handleRowClick}
							pagination
							getRowClassName={(p) => (p.row.markedForReview ? 'error' : '')}
							onReload={() => {
								refetchGroups();
								queryClient.invalidateQueries({ queryKey: ['personal-groups'] });
							}}
						/>
					</Paper>
				</div>
			</div>
			{isFormOpened && (
				<GroupForm
					isOpen={true}
					data={defaultGroupData}
					onClose={handleCloseForm}
					onSubmit={handleAddGroup}
					types={types}
					roles={roles}
				/>
			)}
		</>
	);
}
